import { useState } from "react";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { lastDayOfMonth, endOfQuarter } from "date-fns";

export interface DateRange {
  from: Date;
  to: Date;
}

interface DateRangeFilterProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  className?: string;
}

const MONTHS_SHORT_PL = ["Sty", "Lut", "Mar", "Kwi", "Maj", "Cze", "Lip", "Sie", "Wrz", "Paź", "Lis", "Gru"];
const MONTHS_FULL_PL = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
];

function getMonthRange(year: number, month: number): DateRange {
  const from = new Date(year, month, 1);
  const to = lastDayOfMonth(from);
  return { from, to };
}

function getQuarterRange(year: number, quarter: number): DateRange {
  const startMonth = (quarter - 1) * 3;
  const from = new Date(year, startMonth, 1);
  const to = endOfQuarter(from);
  return { from, to };
}

function getYearRange(year: number): DateRange {
  return {
    from: new Date(year, 0, 1),
    to: new Date(year, 11, 31),
  };
}

function formatSelectionLabel(range: DateRange): string {
  const fromMonth = range.from.getMonth();
  const toMonth = range.to.getMonth();
  const fromYear = range.from.getFullYear();
  const toYear = range.to.getFullYear();
  const fromDay = range.from.getDate();
  const toDay = range.to.getDate();
  const lastDayOfToMonth = lastDayOfMonth(range.to).getDate();

  // Check if this is a full month selection (starts on 1st, ends on last day)
  const isFullMonthStart = fromDay === 1;
  const isFullMonthEnd = toDay === lastDayOfToMonth;

  if (!isFullMonthStart || !isFullMonthEnd) {
    // Not aligned to months, show full dates
    const fromStr = `${fromDay}.${String(fromMonth + 1).padStart(2, "0")}.${fromYear}`;
    const toStr = `${toDay}.${String(toMonth + 1).padStart(2, "0")}.${toYear}`;
    return `${fromStr} - ${toStr}`;
  }

  // Single month: "Styczeń 2025"
  if (fromMonth === toMonth && fromYear === toYear) {
    return `${MONTHS_FULL_PL[fromMonth]} ${fromYear}`;
  }

  // Full year: "Cały rok 2025"
  if (fromMonth === 0 && toMonth === 11 && fromYear === toYear) {
    return `Cały rok ${fromYear}`;
  }

  // Quarter detection
  if (fromYear === toYear && (toMonth - fromMonth) === 2 && fromMonth % 3 === 0) {
    const quarter = Math.floor(fromMonth / 3) + 1;
    return `Q${quarter} ${fromYear}`;
  }

  // Range within same year: "Lut - Maj 2025"
  if (fromYear === toYear) {
    return `${MONTHS_SHORT_PL[fromMonth]} - ${MONTHS_SHORT_PL[toMonth]} ${fromYear}`;
  }

  // Cross-year range: "Gru 2024 - Mar 2025"
  return `${MONTHS_SHORT_PL[fromMonth]} ${fromYear} - ${MONTHS_SHORT_PL[toMonth]} ${toYear}`;
}

export function DateRangeFilter({ dateRange, onDateRangeChange, className }: DateRangeFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [displayYear, setDisplayYear] = useState(() => dateRange.from.getFullYear());
  const [mode, setMode] = useState<"select" | "range">("select");
  const [rangeStart, setRangeStart] = useState<{ year: number; month: number } | null>(null);

  const prevYear = () => setDisplayYear((y) => y - 1);
  const nextYear = () => setDisplayYear((y) => y + 1);

  const isMonthFullySelected = (year: number, month: number): boolean => {
    const monthStart = new Date(year, month, 1);
    const monthEnd = lastDayOfMonth(monthStart);
    return dateRange.from <= monthStart && dateRange.to >= monthEnd;
  };

  const isMonthInRange = (year: number, month: number): boolean => {
    const monthStart = new Date(year, month, 1);
    const monthEnd = lastDayOfMonth(monthStart);
    return dateRange.from <= monthEnd && dateRange.to >= monthStart;
  };

  const isMonthRangeStart = (year: number, month: number): boolean => {
    if (!rangeStart) return false;
    return rangeStart.year === year && rangeStart.month === month;
  };

  const handleMonthClick = (year: number, month: number) => {
    if (mode === "select") {
      // Normal mode: select single month
      const range = getMonthRange(year, month);
      onDateRangeChange(range);
      setIsOpen(false);
    } else {
      // Range mode
      if (!rangeStart) {
        // First click: set start
        setRangeStart({ year, month });
      } else {
        // Second click: set end and apply
        const startDate = new Date(rangeStart.year, rangeStart.month, 1);
        const endDate = new Date(year, month, 1);

        let from: Date, to: Date;
        if (startDate <= endDate) {
          from = startDate;
          to = lastDayOfMonth(endDate);
        } else {
          from = endDate;
          to = lastDayOfMonth(startDate);
        }

        onDateRangeChange({ from, to });
        setRangeStart(null);
        setMode("select");
        setIsOpen(false);
      }
    }
  };

  const handleQuarterClick = (quarter: number) => {
    const range = getQuarterRange(displayYear, quarter);
    onDateRangeChange(range);
    setIsOpen(false);
  };

  const handleYearClick = () => {
    const range = getYearRange(displayYear);
    onDateRangeChange(range);
    setIsOpen(false);
  };

  const toggleRangeMode = () => {
    if (mode === "select") {
      setMode("range");
      setRangeStart(null);
    } else {
      setMode("select");
      setRangeStart(null);
    }
  };

  const getMonthButtonVariant = (year: number, month: number): "default" | "outline" | "secondary" => {
    if (mode === "range" && isMonthRangeStart(year, month)) {
      return "default";
    }
    if (mode === "select" && isMonthFullySelected(year, month)) {
      return "default";
    }
    if (mode === "select" && isMonthInRange(year, month)) {
      return "secondary";
    }
    return "outline";
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Popover open={isOpen} onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          setMode("select");
          setRangeStart(null);
        }
      }}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="justify-start text-left font-normal min-w-[200px]">
            <Calendar className="mr-2 h-4 w-4" />
            {formatSelectionLabel(dateRange)}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-4" align="start">
          <div className="space-y-4">
            {/* Range mode indicator */}
            {mode === "range" && (
              <div className="text-sm text-muted-foreground text-center pb-2 border-b">
                {rangeStart
                  ? "Kliknij miesiąc końcowy"
                  : "Kliknij miesiąc początkowy"}
              </div>
            )}

            {/* Year Navigation */}
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="icon" onClick={prevYear}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="font-semibold text-lg">{displayYear}</span>
              <Button variant="ghost" size="icon" onClick={nextYear}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Month Grid 4x3 */}
            <div className="grid grid-cols-4 gap-2">
              {MONTHS_SHORT_PL.map((monthName, i) => (
                <Button
                  key={i}
                  variant={getMonthButtonVariant(displayYear, i)}
                  size="sm"
                  className="h-9"
                  onClick={() => handleMonthClick(displayYear, i)}
                >
                  {monthName}
                </Button>
              ))}
            </div>

            {/* Separator */}
            <div className="border-t" />

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2 justify-center">
              {[1, 2, 3, 4].map((q) => (
                <Button
                  key={q}
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuarterClick(q)}
                  disabled={mode === "range"}
                >
                  Q{q}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={handleYearClick}
                disabled={mode === "range"}
              >
                Cały rok
              </Button>
            </div>

            {/* Separator */}
            <div className="border-t" />

            {/* Custom Range Toggle */}
            <Button
              variant={mode === "range" ? "secondary" : "ghost"}
              size="sm"
              className="w-full"
              onClick={toggleRangeMode}
            >
              <Calendar className="mr-2 h-4 w-4" />
              {mode === "range" ? "Anuluj wybór zakresu" : "Własny zakres miesięcy..."}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Default date range (2025 - when business data exists)
export const DEFAULT_DATE_RANGE: DateRange = {
  from: new Date(2025, 0, 1),
  to: new Date(2025, 11, 31),
};

// Get current month date range
export function getCurrentMonthRange(): DateRange {
  const now = new Date();
  return {
    from: new Date(now.getFullYear(), now.getMonth(), 1),
    to: new Date(now.getFullYear(), now.getMonth() + 1, 0), // Last day of month
  };
}

// Helper to check if a date is within range
export function isDateInRange(dateStr: string | null | undefined, range: DateRange): boolean {
  if (!dateStr) return false;

  let date: Date;

  // Try DD.MM.YYYY format (Polish)
  if (dateStr.includes(".")) {
    const parts = dateStr.split(".");
    if (parts.length !== 3) return false;
    const [day, month, year] = parts.map(Number);
    date = new Date(year, month - 1, day);
  }
  // Try YYYY-MM-DD format (ISO)
  else if (dateStr.includes("-")) {
    const parts = dateStr.split("-");
    if (parts.length !== 3) return false;
    const [year, month, day] = parts.map(Number);
    date = new Date(year, month - 1, day);
  }
  else {
    return false;
  }

  return date >= range.from && date <= range.to;
}
